import { MedplumClient } from "@medplum/core";
import {
  Practitioner,
  QuestionnaireResponse,
  Reference,
  ValueSet,
  ValueSetExpansionContains,
} from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";

import { CUSTOM_TAG } from "@/fhir/constants/tags";
import { NonCustomCategories } from "@/shared/constants/fee-schedule-categories";
import { RETIRED_PROCEDURE_CODES } from "@/shared/procedure-code-effective-dates";
import { formatCustomProcedureOptionDisplay } from "@/shared/utils/custom-procedure-option-display";
import { setAccountsBestEffort } from "@/utils/accounts";
import { getCombinedProcedureCodesValueSetUrl } from "@/utils/billing";
export { getCombinedProcedureCodesValueSetUrl };
import { processWithConcurrencyLimit } from "@/utils/concurrency";
import { getOrganizationReference, joinReferenceString } from "@/utils/medplum";

import { COMBINED_VALUE_SET_EXPAND_CONCURRENCY } from "../constants/combined-procedure-codes";
import { CUSTOM_PROCEDURE_CODE_SYSTEM } from "../constants/custom-codes";
import { NON_CUSTOM_CATEGORY_VALUE_SETS } from "../constants/fee-table";
import { ProcedureCode } from "../types/fee-table";
import {
  mergeAndDeduplicateCodes,
  pickStoredProcedureCodesOfValueSet,
} from "../utils/merge-procedure-codes";

import { loadCustomProcedureCodes } from "./custom-codes";
import { findLatestCombinedValueSet } from "./find-latest-combined-value-set";
import {
  fetchProcedureFeeQuestionnaire,
  fetchProcedureFeeResponsesByCode,
  loadProcedureCodesFromValueSet,
  parseProcedureFeeQuestionnaireResponse,
} from "./procedure-fees";

/**
 * Maps a ProcedureCode to ValueSetExpansionContains format
 */
function toValueSetContains(code: ProcedureCode, system: string): ValueSetExpansionContains {
  return {
    system,
    code: code.code,
    display: code.name,
  };
}

/** A rebuilt combined ValueSet and the fee schedule categories it could not include. */
export type CombinedProcedureCodesRebuild = {
  valueSet: ValueSet;
  /** Categories whose ValueSet failed to expand, so their codes are missing from the expansion. */
  unexpandedCategories: NonCustomCategories[];
};

type NonCustomProcedureCodes = {
  procedureCodes: ProcedureCode[];
  unexpandedValueSetUrls: Set<string>;
};

/**
 * Maps a fee schedule code to ValueSetExpansionContains format. A code the practice switched off
 * is kept with `inactive: true`, so the CPT search can hide the same code from its results.
 */
function toFeeValueSetContains(procedureCode: ProcedureCode): ValueSetExpansionContains {
  const contains = toValueSetContains(procedureCode, procedureCode.coding?.system ?? "");
  return procedureCode.isActive ? contains : { ...contains, inactive: true };
}

/**
 * Loads the fee schedule codes of every fee ValueSet, with the practice's active status.
 * Expands each ValueSet with a bounded concurrency limit to reduce wall-clock time
 * without opening ~20 simultaneous expand requests (settings path; not scheduling availability).
 * A code the practice switched off is inactive in every ValueSet that lists it, because fee
 * responses are matched by code.
 * A ValueSet that fails to expand falls back to the codes the last rebuild stored for it, with the
 * current fee responses applied, so a switched-off code stays inactive until the next rebuild.
 * @param medplum The Medplum client
 * @param organizationReference Practice reference used to load the fee responses
 * @param storedValueSet The combined ValueSet saved by the last rebuild, if any
 * @returns The fee codes, active and switched off, without retired codes, and the URLs of the
 * ValueSets that failed to expand
 */
async function loadFeeProcedureCodes(
  medplum: MedplumClient,
  organizationReference: string | undefined,
  storedValueSet: ValueSet | undefined,
): Promise<NonCustomProcedureCodes> {
  const profile = await medplum.getProfile();
  let practitionerReference: Reference<Practitioner> | undefined;

  if (profile?.resourceType === "Practitioner" && profile.id) {
    practitionerReference = {
      reference: joinReferenceString("Practitioner", profile.id),
    };
  }

  const questionnaire = await fetchProcedureFeeQuestionnaire(medplum);
  let responsesByCode: Record<string, QuestionnaireResponse> = {};

  if (questionnaire && (organizationReference || practitionerReference)) {
    responsesByCode = await fetchProcedureFeeResponsesByCode(
      medplum,
      questionnaire,
      practitionerReference,
      organizationReference,
    );
  }

  const applyFeeStatus = (procedureCodes: ProcedureCode[]) =>
    procedureCodes.reduce<ProcedureCode[]>((codesWithStatus, procedure) => {
      if (!RETIRED_PROCEDURE_CODES.has(procedure.code)) {
        codesWithStatus.push(
          parseProcedureFeeQuestionnaireResponse(responsesByCode[procedure.code], procedure),
        );
      }
      return codesWithStatus;
    }, []);

  const codesByValueSet = await processWithConcurrencyLimit(
    Object.values(NON_CUSTOM_CATEGORY_VALUE_SETS),
    async (url) => {
      try {
        const procedureCodes = await loadProcedureCodesFromValueSet(medplum, url);
        return { url, procedureCodes: applyFeeStatus(procedureCodes), hasExpanded: true };
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: "combined-procedure-codes" },
          extra: { operation: "expand-procedure-codes-value-set", valueSetUrl: url },
        });
        const storedCodes = pickStoredProcedureCodesOfValueSet(storedValueSet, url);
        return { url, procedureCodes: applyFeeStatus(storedCodes), hasExpanded: false };
      }
    },
    COMBINED_VALUE_SET_EXPAND_CONCURRENCY,
  );

  return codesByValueSet.reduce<NonCustomProcedureCodes>(
    (result, { url, procedureCodes, hasExpanded }) => {
      result.procedureCodes.push(...procedureCodes);
      if (!hasExpanded) result.unexpandedValueSetUrls.add(url);
      return result;
    },
    { procedureCodes: [], unexpandedValueSetUrls: new Set() },
  );
}

/**
 * Loads active custom procedure codes and maps them to ValueSetExpansionContains
 */
async function loadActiveCustomProcedureCodes(
  medplum: MedplumClient,
  practiceId: string,
  codeSystemUrl: string,
): Promise<ValueSetExpansionContains[]> {
  try {
    const customProcedureCodes = await loadCustomProcedureCodes(medplum, practiceId);
    return customProcedureCodes
      .filter((code) => code.isActive)
      .map((code) => ({
        ...toValueSetContains(code, codeSystemUrl),
        display: formatCustomProcedureOptionDisplay(code.name),
      }));
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: "combined-procedure-codes" },
      extra: {
        operation: "load-active-custom-procedure-codes",
        practiceId,
        codeSystemUrl,
      },
    });
    return [];
  }
}

type PracticeRebuildQueueState = {
  chain: Promise<CombinedProcedureCodesRebuild> | undefined;
  trailingRequested: boolean;
};

const rebuildQueueByPracticeId = new Map<string, PracticeRebuildQueueState>();

function getPracticeRebuildQueueState(practiceId: string): PracticeRebuildQueueState {
  let state = rebuildQueueByPracticeId.get(practiceId);
  if (!state) {
    state = { chain: undefined, trailingRequested: false };
    rebuildQueueByPracticeId.set(practiceId, state);
  }
  return state;
}

/**
 * Clears in-memory rebuild queue state. For unit tests only.
 */
export function resetCombinedProcedureCodesRebuildQueueForTests(): void {
  rebuildQueueByPracticeId.clear();
}

async function runQueuedCombinedProcedureCodesRebuild(
  medplum: MedplumClient,
  practiceId: string,
): Promise<CombinedProcedureCodesRebuild> {
  const state = getPracticeRebuildQueueState(practiceId);
  let lastResult: CombinedProcedureCodesRebuild;

  do {
    state.trailingRequested = false;
    lastResult = await rebuildCombinedProcedureCodesValueSet(medplum, practiceId);
  } while (state.trailingRequested);

  return lastResult;
}

function enqueueCombinedProcedureCodesValueSetRebuild(
  medplum: MedplumClient,
  practiceId: string,
): Promise<CombinedProcedureCodesRebuild> {
  const state = getPracticeRebuildQueueState(practiceId);

  if (state.chain) {
    state.trailingRequested = true;
    return state.chain;
  }

  state.chain = runQueuedCombinedProcedureCodesRebuild(medplum, practiceId).finally(() => {
    state.chain = undefined;
  });

  return state.chain;
}

/**
 * Schedules a combined ValueSet rebuild without blocking the caller. Overlapping triggers for the
 * same practice coalesce into one in-flight rebuild plus at most one trailing rerun so stale
 * snapshots cannot overwrite a newer expansion via last-write-wins.
 */
export function scheduleCombinedProcedureCodesValueSetRebuild(
  medplum: MedplumClient,
  practiceId: string,
  onRebuildRejected?: (error: unknown) => void,
): void {
  void enqueueCombinedProcedureCodesValueSetRebuild(medplum, practiceId).catch((error) => {
    onRebuildRejected?.(error);
  });
}

/**
 * Creates or updates a combined ValueSet that includes:
 * - All non-custom procedure fee ValueSets, with codes the practice switched off marked inactive
 * - Custom codes ValueSet for the practice
 *
 * This function expands all included ValueSets and creates a ValueSet with a direct expansion
 * to ensure all codes are searchable. CPT codes are not stored: the hosted CPT ValueSet is searched
 * live, because an unfiltered expansion of it returns only its first page.
 *
 * Concurrent callers for the same practice share one serialized rebuild chain.
 */
export async function getOrCreateCombinedProcedureCodesValueSet(
  medplum: MedplumClient,
  practiceId: string,
): Promise<ValueSet> {
  const { valueSet } = await enqueueCombinedProcedureCodesValueSetRebuild(medplum, practiceId);
  return valueSet;
}

/**
 * Rebuilds the combined ValueSet like `getOrCreateCombinedProcedureCodesValueSet`, and also
 * returns the fee schedule categories whose codes are missing because their ValueSet failed to
 * expand.
 */
export function rebuildCombinedProcedureCodes(
  medplum: MedplumClient,
  practiceId: string,
): Promise<CombinedProcedureCodesRebuild> {
  return enqueueCombinedProcedureCodesValueSetRebuild(medplum, practiceId);
}

/**
 * Rebuilds the practice combined procedure codes ValueSet from current server data.
 */
async function rebuildCombinedProcedureCodesValueSet(
  medplum: MedplumClient,
  practiceId: string,
): Promise<CombinedProcedureCodesRebuild> {
  const valueSetUrl = getCombinedProcedureCodesValueSetUrl(practiceId);
  const organizationReference = getOrganizationReference(practiceId);

  const existingValueSet = await findLatestCombinedValueSet(
    medplum,
    valueSetUrl,
    organizationReference,
  );

  const { procedureCodes: feeCodes, unexpandedValueSetUrls } = await loadFeeProcedureCodes(
    medplum,
    organizationReference,
    existingValueSet,
  );
  const feeContains = feeCodes.map(toFeeValueSetContains);

  const codeSystemUrl = `${CUSTOM_PROCEDURE_CODE_SYSTEM}/${practiceId}`;
  const customContains = await loadActiveCustomProcedureCodes(medplum, practiceId, codeSystemUrl);

  const contains = mergeAndDeduplicateCodes(feeContains, customContains);

  // Build the combined ValueSet with ONLY expansion (no compose)
  // When a ValueSet has both compose and expansion, Medplum's valueSetExpand filter
  // might use compose to expand dynamically, which doesn't work correctly with nested ValueSets.
  // By using only expansion, the filter will work directly on the pre-expanded codes.
  const valueSet: ValueSet = {
    resourceType: "ValueSet",
    url: valueSetUrl,
    name: "Combined Procedure Codes",
    title: "Combined Procedure Codes",
    status: "active",
    expansion: {
      timestamp: new Date().toISOString(),
      contains,
    },
    meta: {
      tag: [CUSTOM_TAG],
    },
  };

  const combinedValueSet = await (existingValueSet?.id
    ? medplum.updateResource({ ...valueSet, id: existingValueSet.id })
    : medplum.createResource(valueSet));

  await setAccountsBestEffort(medplum, combinedValueSet, [{ reference: organizationReference }]);

  const unexpandedCategories = Object.values(NonCustomCategories).filter((category) =>
    unexpandedValueSetUrls.has(NON_CUSTOM_CATEGORY_VALUE_SETS[category]),
  );
  return { valueSet: combinedValueSet, unexpandedCategories };
}
